// ─────────────────────────────────────────────────────────────────────────────
// mackInputsView.js — THE READ-ONLY VIEW MODEL for Mack's Inputs decision interface (owner, 2026-10-14).
//
// FRONT END ONLY. It starts after GET /api/mack/inputs has answered and reads the RAW response — the
// existing parser (lib/mackInputs.ts) turns any state it does not know into "UNPROVEN", which is the
// front end deciding a state; here every code is the backend's own, through one table, and an unknown
// code shows as itself. It groups, counts and formats; it never fetches, writes or computes.
//
//   picture A (the original, rexy-app InputsTab.tsx before cf50671): "Inputs by country", the six
//   sheet chips, the macro-variable card grid, the instruments table.
//   Grouping: the response's own `group` (macro_variables / instruments) and `sheet`; instruments
//   are rows by `declared_population.instrument`, metrics by the identity's last segment
//   (last_price / daily_move / sigma_daily / normal_turnover). Anything else → Unassigned.
//
// PLAIN JS: the Trading web and iOS previews carry this file byte for byte.
// ─────────────────────────────────────────────────────────────────────────────
import { STATE_LANGUAGE, CODE_WORDS, codeWords, whenText, kvLines, referenceText, ageText, readableNumber, siteName } from './mackCalculatorView';

const own = (obj, key) => obj != null && key != null && Object.prototype.hasOwnProperty.call(obj, key);
function distinct(list) { return list.filter((x, i) => list.indexOf(x) === i); }

// Inputs' own codes, in words (an unknown code still shows as itself).
export const INPUTS_WORDS = {
  CURRENT: 'Current', AVAILABLE: 'Available', UNPROVEN: 'Unproven', UNAVAILABLE: 'Unavailable',
  FORMULA_PASS_2_PENDING: 'Awaiting Formula Pass 2', AWAITING_FORMULA_PASS_2: 'Awaiting Formula Pass 2',
  OWNER_ACTIVATION_REQUIRED: 'Owner activation required', ACTIVE: 'Active',
  LIVE_PARTIAL_SOURCE_UNAVAILABLE: 'Live — some sources unavailable',
  DIRECT_SOURCE_ACQUIRED: 'Direct source', FORMULA_DERIVED: 'Formula derived',
  SOURCE_HTTP_403: 'Source refused the request (HTTP 403)',
  SP_GLOBAL_PMI_SUBSCRIPTION_REQUIRED: 'S&P Global PMI subscription required',
  CURRENT_PUBLICATION_IDENTITY_VALIDATED_NEXT_PUBLICATION_NOT_EXPOSED: 'Current publication validated; next publication not exposed',
  LATEST_NAMED_PUBLISHER_EDITION_VALIDATED: 'Latest named publisher edition validated',
  LATEST_ADMITTED_REFERENCE_PERIOD_CURRENT_FRESHNESS_UNPROVEN: 'Latest admitted reference period; freshness unproven',
  EXACT_FRED_H15_LATEST_REFERENCE_AND_DATASET_EDITION_VALIDATED: 'FRED H.15 latest reference and dataset edition validated',
  DATED_BIS_WEEKLY_POLICY_LEVEL_CURRENT_THROUGH_REFERENCE_ONLY: 'BIS weekly policy level, current through its reference date only',
  CURRENT_SOURCE_LATEST_METADATA_AND_NEXT_RELEASE_VALIDATED: 'Latest source metadata and next release validated',
  CURRENT_EXPECTED_COMPLETED_SOURCE_PERIOD_VALIDATED: 'Expected completed source period validated',
  NO_DIRECT_RAW_EVENT_BINDING: 'No direct raw event binding',
};
export function inputsWords(code) {
  if (code === null || code === undefined || code === '') return '—';
  if (own(INPUTS_WORDS, code)) return INPUTS_WORDS[code];
  if (own(STATE_LANGUAGE, code) || own(CODE_WORDS, code)) return codeWords(code);
  return String(code);
}
// The dot beside the word (never colour alone): good / waiting / failure, from the backend's code.
export const TONE = { CURRENT: 'good', AVAILABLE: 'good', SOURCE_UNAVAILABLE: 'bad', PROVIDER_ACCESS_REQUIRED: 'bad', UNAVAILABLE: 'bad', REFUSED: 'bad', VALIDATION_REFUSED: 'bad' };
export function toneOf(code) { return own(TONE, code) ? TONE[code] : 'wait'; }

// picture A's sheet labels, matched to the response's sheet codes by identity.
export const SHEET_LABELS = { USD: 'US', GBP: 'UK', EUR: 'EUR', JPY: 'JPY', AUD: 'AUD', BLOC: 'Reserve bloc' };
export const SHEET_ORDER = ['USD', 'GBP', 'EUR', 'JPY', 'AUD', 'BLOC'];
// The short unit a card may show (the exact unit is in the drawer). Only these; anything else shows its own words small.
export const SHORT_UNITS = { percent: '%' };
export const METRICS = [
  { key: 'last_price', label: 'Last' }, { key: 'daily_move', label: 'Daily move' },
  { key: 'sigma_daily', label: 'Σ Daily' }, { key: 'normal_turnover', label: 'Turnover' },
];

export const EXPECTED_INPUTS = {
  success: true, formula_calls: 0, experience_entries_written: 0, runtime_broad_search_count: 0,
  calculator_invoked: false, preview_only: false,
};

// ── THE FIELD LEDGER ────────────────────────────────────────────────────────
export const INPUTS_LEDGER = {
  top: {
    success: 'notice if not true', state: 'status band (words) · Response details',
    variables: 'cards / instrument rows / Unassigned, one per variable per sheet', components: 'per-sheet fold "Raw components"',
    groups: 'Response details (grouping uses each variable\'s own group)', currencies: 'Response details (the selector uses the variables\' sheets)',
    history_workbook: 'status band fold · Response details', inventory_totals: 'status band fold · Response details',
    operational_observation_count: 'status band fold · Response details', acquired_count: 'status band fold · Response details',
    native_t2_record_count: 'Response details', history_count: 'Response details', updated_at: 'status band (last update, Hobart) · Response details',
    latest_change: 'Response details', gaps: 'status band fold "N incomplete contracts" (compact rows)', currency_contracts: 'Response details',
    pulse: 'status band chip (state in words) and line (schedule, last attempt in Hobart); drawer "Pulse and Calendar" per variable; Response details',
    workbook_boundary: 'Response details', experience_entries_written: 'notice if not 0 · Response details', calculator_invoked: 'notice if not false · Response details',
    runtime_broad_search_count: 'notice if not 0 · Response details', formula_calls: 'notice if not 0 · Response details',
    preview_only: 'the "preview rows" line shows only if true · Response details', installed_t1: 'Response details',
    freshness_matrix: 'Response details', application_projection: 'card footer (ingest cadence, matched by sheet + name) · Response details',
  },
  variable: {
    observation: ['value', 'raw_value', 'unit', 'state', 'reason', 'reference_period', 'observed_at', 'publication_at', 'effective_at', 'retrieved_at', 'latest_change', 'last_valid_value', 'last_valid_reference_period', 'timestamp_precision', 'last_attempt_at'],
    source: ['publisher', 'source_url', 'variable_id', 'field_id', 'series_id', 'source_id', 'freshness_state', 'freshness_evidence', 'source_state', 'source_classification', 'operational_state', 'diagnostic_state', 'formula_input_state', 'formula'],
    pulse_calendar: ['pulse_state', 'calendar_event_id'],
    destinations: ['named_destinations'], consumers: ['downstream_consumers'],
    history: ['history_count', 'vintage_count', 'history', 'observation'],
    grouping: ['name', 'group', 'country', 'currency', 'sheet', 'block', 'declared_population'],
    raw_leaves: ['required_raw_leaves', 'available_raw_leaves', 'unavailable_raw_leaves', 'current_eligible_raw_leaves', 'raw_leaf_state', 'required_raw_leaf_details', 'required_raw_reference_period_gaps'],
  },
};
// Ledger notes — what this view knowingly does not carry yet (owner, 2026-10-14 second polish).
export const INPUTS_LEDGER_NOTES = [
  'Acquisition-evidence fold (the Mack chat\'s work in progress inside the old Inputs tab): reachable through "Full evidence view" at the foot of the page, rendered as it is. It is carried into this view once it is committed.',
  'Acquire Inputs: the evidence view\'s own control, request and feedback, carried unchanged (POST /api/mack/inputs/refresh, pressed only by the owner).',
];
const VARIABLE_NAMED = new Set(Object.values(INPUTS_LEDGER.variable).flat());
const TOP_NAMED = new Set(Object.keys(INPUTS_LEDGER.top));
export function inputsOtherFields(obj, level) {
  if (!obj || typeof obj !== 'object') return [];
  const named = level === 'variable' ? VARIABLE_NAMED : TOP_NAMED;
  return Object.keys(obj).filter((k) => !named.has(k));
}

// ── READ (never blank) ──────────────────────────────────────────────────────
export function readInputs(raw) {
  const notices = [];
  const p = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  if (!raw || typeof raw !== 'object') notices.push('Inputs could not be read from the server.');
  Object.keys(EXPECTED_INPUTS).forEach((k) => {
    if (!own(p, k)) { if (raw) notices.push(`The response has no ${k}.`); }
    else if (p[k] !== EXPECTED_INPUTS[k]) notices.push(`${k} is ${JSON.stringify(p[k])}, expected ${JSON.stringify(EXPECTED_INPUTS[k])}.`);
  });
  if (raw && !Array.isArray(p.variables)) notices.push('The response has no variables list.');
  const unknownStates = distinct((Array.isArray(p.variables) ? p.variables : []).map((v) => v && v.state)
    .filter((c) => c && !own(INPUTS_WORDS, c) && !own(STATE_LANGUAGE, c) && !own(CODE_WORDS, c)));
  if (unknownStates.length) notices.push(`State codes not in the table, shown as received: ${unknownStates.join(', ')}.`);
  return { response: p, notices };
}

// ── THE MODEL ───────────────────────────────────────────────────────────────
const sheetsOf = (v) => String((v && v.sheet) || '').split(',').map((s) => s.trim()).filter(Boolean);
function metricOf(v) { const id = String(v.variable_id || v.field_id || ''); return id.slice(id.lastIndexOf('.') + 1); }
function instrumentOf(v) { return (v.declared_population && v.declared_population.instrument) || null; }

export function cardView(v, response, now) {
  const tabs = response && response.application_projection && Array.isArray(response.application_projection.country_tabs) ? response.application_projection.country_tabs : [];
  const tab = tabs.find((t) => sheetsOf(v).includes(t.label));
  const apCard = tab && Array.isArray(tab.cards) ? tab.cards.find((c) => c.label === v.name) : null;
  const has = v.value !== null && v.value !== undefined && v.value !== '';
  const unit = v.unit || '';
  return {
    id: v.variable_id, name: v.name, stateCode: v.state, stateWord: inputsWords(v.state), tone: toneOf(v.state),
    value: has ? readableNumber(v.value, unit) : '—', exact: has ? String(v.value) : null, unitShort: has && own(SHORT_UNITS, unit) ? SHORT_UNITS[unit] : '', unitText: has && !own(SHORT_UNITS, unit) ? unit : '',
    publisher: v.publisher || '—', age: ageText(v.retrieved_at, now),
    footer: apCard && apCard.ingest_cadence ? `▸ INGEST · ${apCard.ingest_cadence}` : [referenceText(v.reference_period), v.frequency || null].filter((x) => x && x !== '—').join(' · ') || '—',
    variable: v,
  };
}

export function buildInputsModel(response, now) {
  const r = response || {};
  const vars = Array.isArray(r.variables) ? r.variables.filter((v) => v && typeof v === 'object') : [];
  const comps = Array.isArray(r.components) ? r.components.filter((v) => v && typeof v === 'object') : [];
  const found = distinct(vars.flatMap(sheetsOf));
  const sheets = [...SHEET_ORDER.filter((s) => found.includes(s)), ...found.filter((s) => !SHEET_ORDER.includes(s))]
    .map((code) => ({ code, label: own(SHEET_LABELS, code) ? SHEET_LABELS[code] : code }));
  const pulse = r.pulse && typeof r.pulse === 'object' ? r.pulse : null;
  const gaps = Array.isArray(r.gaps) ? r.gaps : [];
  const status = {
    state: r.state || null, stateWords: inputsWords(r.state),
    pulseState: pulse ? pulse.state : null, pulseWords: pulse ? inputsWords(pulse.state) : 'No Pulse in the response',
    schedule: pulse ? pulse.schedule || null : null, timezone: pulse ? pulse.timezone || null : null,
    lastAttempt: pulse ? whenText(pulse.last_at) : whenText(null), nextAt: pulse ? whenText(pulse.next_at) : whenText(null),
    updated: whenText(r.updated_at),
    gaps: gaps.map((g) => ({ subject: g.subject || g.variable_id || g.field_id || 'Inputs contract', id: g.variable_id || g.field_id || null,
      state: inputsWords(g.state), stateCode: g.state || null, reason: inputsWords(g.reason), reasonCode: g.reason || null,
      missing: Array.isArray(g.missing_fields) ? g.missing_fields : [] })),
    previewLine: r.preview_only === true, oneOwner: r.one_owner_per_variable === true,
    totals: r.inventory_totals && typeof r.inventory_totals === 'object' ? r.inventory_totals : {},
  };
  const sheetView = (code) => {
    const mine = vars.filter((v) => sheetsOf(v).includes(code));
    const macro = mine.filter((v) => v.group === 'macro_variables').map((v) => cardView(v, r, now));
    const instVars = mine.filter((v) => v.group === 'instruments' && instrumentOf(v));
    const rows = {};
    instVars.forEach((v) => {
      const key = instrumentOf(v);
      if (!own(rows, key)) rows[key] = { instrument: key, metrics: {}, other: [] };
      const m = metricOf(v);
      if (METRICS.some((x) => x.key === m) && !own(rows[key].metrics, m)) rows[key].metrics[m] = v; else rows[key].other.push(v);
    });
    const instruments = Object.values(rows).map((row) => {
      const last = row.metrics.last_price || null;
      return { ...row, age: last ? ageText(last.retrieved_at, now) : '—', state: last ? inputsWords(last.state) : '—', stateCode: last ? last.state : null, tone: last ? toneOf(last.state) : 'wait' };
    });
    const columns = METRICS.filter((m) => instruments.some((row) => own(row.metrics, m.key)));
    const unassigned = mine.filter((v) => !(v.group === 'macro_variables' || (v.group === 'instruments' && instrumentOf(v))))
      .concat(instruments.flatMap((row) => row.other));
    return { code, macro, instruments, columns, unassigned: unassigned.map((v) => cardView(v, r, now)),
      components: comps.filter((c) => sheetsOf(c).includes(code)).map((c) => cardView(c, r, now)),
      variableCount: mine.length };
  };
  return { status, sheets, sheetView, variableCount: vars.length, componentCount: comps.length, topOther: inputsOtherFields(r, 'top') };
}

// ── THE DRAWER for one variable (Part A's rules) ────────────────────────────
function groupDestinations(list) {
  const groups = [];
  list.forEach((d) => {
    const x = typeof d === 'string' ? { field: d, sheet: '—', block: '—' }
      : { field: d.named_input || d.workbook_named_input || d.field || d.name || '—', sheet: d.sheet || '—', block: d.block || '—' };
    let g = groups.find((y) => y.sheet === x.sheet && y.block === x.block);
    if (!g) { g = { sheet: x.sheet, block: x.block, fields: [] }; groups.push(g); }
    g.fields.push(x.field);
  });
  return groups.map((g) => ({ ...g, title: `${g.sheet} · ${g.block} — ${g.fields.length} ${g.fields.length === 1 ? 'field' : 'fields'}` }));
}
function row(label, text, exact, href) { return { label, text: text === undefined || text === null || text === '' ? '—' : String(text), exact: exact || null, href: href || null }; }
function whenRow(label, value) { const w = whenText(value); return row(label, w.text, w.exact); }
export function variableView(v, response) {
  const r = response || {};
  const plans = r.pulse && Array.isArray(r.pulse.plans) ? r.pulse.plans : [];
  const plan = plans.find((p) => p.field_id === v.variable_id || p.field_id === v.field_id) || null;
  const has = v.value !== null && v.value !== undefined && v.value !== '';
  const latest = v.latest_change && typeof v.latest_change === 'object' ? kvLines(v.latest_change).map(([k, x]) => `${k}: ${x}`).join(' · ') : v.latest_change;
  const latestWhen = typeof latest === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(latest) ? whenText(latest) : { text: latest, exact: null };
  return {
    id: v.variable_id, title: v.name, value: has ? `${readableNumber(v.value, v.unit)}${v.unit ? ' ' + v.unit : ''}` : '—', state: inputsWords(v.state),
    sections: [
      { title: 'Observation', rows: [
        // Formatted for reading; the exact stored value (and the raw value) beside it.
        row('Value', has ? `${readableNumber(v.value, v.unit)}${v.unit ? ' ' + v.unit : ''}` : inputsWords(v.state),
          has ? [`stored ${v.value}`, v.raw_value !== undefined ? `raw ${v.raw_value}` : null].filter(Boolean).join(' · ') : null),
        row('State', inputsWords(v.state) + (v.reason ? ` — ${v.reason}` : ''), v.state),
        row('Reference period', referenceText(v.reference_period), v.reference_period || null),
        whenRow('Observation', v.observed_at),
        whenRow('Publication', v.publication_at),
        whenRow('Effective', v.effective_at),
        whenRow('Last acquired', v.retrieved_at),
        row('Latest change', latestWhen.text, latestWhen.exact),
      ] },
      { title: 'Source', rows: [
        row('Publisher', v.publisher, null, v.source_url || null),
        row('Source', v.source_url ? (siteName(v.source_url) || v.source_url) : null, null, v.source_url || null),
        row('Mack identity', v.variable_id),
        row('Series', v.series_id && typeof v.series_id === 'object' ? kvLines(v.series_id).map(([k, x]) => `${k}: ${x}`).join(' · ') : v.series_id),
        row('Source id', v.source_id),
        row('Freshness', inputsWords(v.freshness_state), v.freshness_state || null),
      ] },
      { title: 'Pulse and Calendar', rows: [
        row('Inputs Pulse', inputsWords(v.pulse_state), v.pulse_state || null),
        row('Pulse plan', plan ? [plan.cadence, plan.schedule].filter(Boolean).join(' · ') : null),
        row('Calendar release', v.calendar_event_id || (plan && plan.calendar_uuid) || null),
      ] },
      { title: 'History', rows: [
        row('Observations', v.history_count === undefined ? null : String(v.history_count)),
        row('Vintages', v.vintage_count === undefined ? null : String(v.vintage_count)),
      ], history: true },
    ],
    destinations: (Array.isArray(v.named_destinations) ? v.named_destinations : []).map((d) => (typeof d === 'string'
      ? { field: d, sheet: '—', block: '—' } : { field: d.named_input || d.workbook_named_input || d.field || d.name || '—', sheet: d.sheet || '—', block: d.block || '—' })),
    // One line per sheet · block ("USD · Policy — 12 fields"), opening to its fields, in the response's order.
    destinationGroups: groupDestinations(Array.isArray(v.named_destinations) ? v.named_destinations : []),
    consumers: (Array.isArray(v.downstream_consumers) ? v.downstream_consumers : []).map((c) => (typeof c === 'string' ? c
      : [[c.tab, c.sheet, c.block].filter(Boolean).join(' · '), c.consumer || c.formula || c.subject].filter(Boolean).join(' — '))),
    allFields: kvLines(v),
  };
}
